import * as ko from 'knockout';

import { app } from '../app';
import * as dashboardApi from '../api/dashboard';
import { NameData } from '../api/names';
import * as trialsApi from '../api/trials';
import i18n from '../i18n';
import { Deferred } from '../utils/deferred';
import { session } from '../session';
import { confirmDialog } from './confirm_dialog';

const template = require('raw-loader!../../templates/components/overview_views_dialog.html').default;

interface VisitLookup {
  templateId: string;
  name: string;
}

interface TraitLookup {
  slug: string;
  name: string;
}

interface ViewsDialogParams {
  result: Deferred<void>;
  trialId: string;
  defaultPresetId: number | null;
  activePresetId: number | null;
  onApply: (preset: dashboardApi.OverviewExportPreset) => void;
  onDefaultChanged: (presetId: number | null) => void;
  onPresetDeleted: (presetId: number) => void;
  sites: NameData[];
  scheduledVisits: VisitLookup[];
  traits: TraitLookup[];
  // Only test subjects are fetched asynchronously when the dialog opens, so this
  // is observable: null means still loading (show the count), a resolved array
  // upgrades to names. Empty cannot be the loading sentinel -- it is needed to
  // mean "loaded, none in this trial" so absent subjects read as "not in this trial".
  testSubjects: ko.Observable<NameData[] | null>;
  // Preloaded synchronously like sites, visits and traits; empty when the trial has none.
  plotDetails: NameData[];
  showIcons: NameData[];
}

interface DetailLine {
  label: string;
  value: string;
}

class OverviewViewsDialog {
  presets = ko.observableArray<dashboardApi.OverviewExportPreset>([]);
  errorMessage = ko.observable('');
  canEdit = session.isAtLeastEditor();
  defaultPresetId = ko.observable<number | null>(null);
  activePresetId = ko.observable<number | null>(null);
  openActionsMenuId = ko.observable<number | null>(null);

  private result: Deferred<void>;
  private trialId: string;
  private onApply: (preset: dashboardApi.OverviewExportPreset) => void;
  private onDefaultChanged: (presetId: number | null) => void;
  private onPresetDeleted: (presetId: number) => void;
  private siteById: Map<string, string>;
  private visitByTemplateId: Map<string, string>;
  private traitBySlug: Map<string, string>;
  private testSubjectById: ko.PureComputed<Map<string, string> | null>;
  private plotDetailById: Map<string, string>;
  private showIconById: Map<string, string>;

  constructor(params: ViewsDialogParams) {
    this.result = params.result;
    this.trialId = params.trialId;
    this.defaultPresetId(params.defaultPresetId);
    this.activePresetId(params.activePresetId);
    this.onApply = params.onApply;
    this.onDefaultChanged = params.onDefaultChanged;
    this.onPresetDeleted = params.onPresetDeleted;
    this.siteById = new Map(params.sites.map((s) => [s.id, s.name]));
    this.visitByTemplateId = new Map(params.scheduledVisits.map((v) => [v.templateId, v.name]));
    this.traitBySlug = new Map(params.traits.map((t) => [t.slug, t.name]));
    this.testSubjectById = ko.pureComputed(() => {
      const testSubjects = params.testSubjects();
      return testSubjects && new Map(testSubjects.map((s) => [s.id, s.name]));
    });
    this.plotDetailById = new Map(params.plotDetails.map((d) => [d.id, d.name]));
    this.showIconById = new Map(params.showIcons.map((c) => [c.id, c.name]));
    this.loadPresets();
  }

  isDefault = (preset: dashboardApi.OverviewExportPreset): boolean =>
    this.defaultPresetId() === preset.id;

  isActive = (preset: dashboardApi.OverviewExportPreset): boolean =>
    this.activePresetId() === preset.id;

  statusLabel = (preset: dashboardApi.OverviewExportPreset): string => {
    const isDefault = this.isDefault(preset);
    const isActive = this.isActive(preset);
    if (isDefault && isActive) return i18n.t('Default - in use')();
    if (isDefault) return i18n.t('Default')();
    if (isActive) return i18n.t('In use')();
    return '';
  };

  detailLines = (preset: dashboardApi.OverviewExportPreset): DetailLine[] => {
    const opts = preset.options;
    const lines: DetailLine[] = [];
    const filters = opts.filters;

    if (filters?.site_ids?.length) {
      lines.push({
        label: i18n.t('Sites')(),
        value: this.resolveNames(filters.site_ids, this.siteById),
      });
    }
    if (filters?.visit_template_ids?.length) {
      lines.push({
        label: i18n.t('Visits')(),
        value: this.resolveNames(filters.visit_template_ids.map(String), this.visitByTemplateId),
      });
    }
    if (filters?.trait_slugs?.length) {
      lines.push({
        label: i18n.t('Traits')(),
        value: this.resolveNames(filters.trait_slugs, this.traitBySlug),
      });
    }
    if (filters?.test_subject_ids?.length) {
      const testSubjectById = this.testSubjectById();
      lines.push({
        label: i18n.t('Test subjects')(),
        value: testSubjectById
          ? this.resolveNames(filters.test_subject_ids.map(String), testSubjectById)
          : i18n.t('{{count}} selected', { count: filters.test_subject_ids.length })(),
      });
    }
    const plotDetailsFilterActive =
      filters?.shown_plot_dimension_meta_ids !== undefined || filters?.show_treatment === false;
    if (filters && plotDetailsFilterActive) {
      const summary = this.plotDetailsSummary(filters);
      lines.push(
        summary !== null
          ? { label: i18n.t('Show plot details')(), value: summary }
          : { label: i18n.t('Hidden plot details')(), value: i18n.t('Filter applied')() }
      );
    }

    lines.push({
      label: i18n.t('Format')(),
      value: opts.export_format === 'csv' ? 'CSV' : 'Excel',
    });

    const extras: string[] = [];
    if (opts.include_site_id) extras.push(i18n.t('site ID')());
    if (opts.include_site_coords) extras.push(i18n.t('site coordinates')());
    if (opts.include_customer_name) extras.push(i18n.t('customer name')());
    if (opts.include_protocol) extras.push(i18n.t('protocol name')());
    if (opts.include_owner) extras.push(i18n.t('owner name')());
    if (opts.include_photo_descriptions) extras.push(i18n.t('photo descriptions')());
    const siteAttributeCount = opts.include_site_attribute_ids?.length ?? 0;
    if (siteAttributeCount > 0) {
      extras.push(i18n.t('{{count}} site attributes', { count: siteAttributeCount })());
    }
    if (extras.length) {
      lines.push({ label: i18n.t('Include')(), value: extras.join(', ') });
    }

    const layout: string[] = [];
    if (opts.visit_handling === 'row') layout.push(i18n.t('visits as rows')());
    if (opts.dimension_handling === 'row') layout.push(i18n.t('dimensions as rows')());
    if (opts.header_layout === 'include_in_trait') {
      layout.push(i18n.t('condensed headers')());
    } else if (opts.header_layout === 'hide') {
      layout.push(i18n.t('hidden trait group row')());
    }
    if (layout.length) {
      lines.push({ label: i18n.t('Layout')(), value: layout.join(', ') });
    }

    if (opts.average_by === 'site_treatment') {
      lines.push({ label: i18n.t('Averaging')(), value: i18n.t('by Site × Treatment')() });
    } else if (opts.average_by === 'site') {
      lines.push({ label: i18n.t('Averaging')(), value: i18n.t('by Site')() });
    }

    if (opts.show_icons_for !== undefined) {
      const iconNames = opts.show_icons_for
        .map((id) => this.showIconById.get(id))
        .filter((name): name is string => !!name);
      lines.push({
        label: i18n.t('Show icons')(),
        value: iconNames.length
          ? iconNames.join(', ')
          : opts.show_icons_for.length
            ? i18n.t('{{count}} selected', { count: opts.show_icons_for.length })()
            : i18n.t('none')(),
      });
    }
    if (opts.picture_size) {
      lines.push({ label: i18n.t('Pics')(), value: opts.picture_size });
    }

    return lines;
  };

  private resolveNames = (ids: string[], lookup: Map<string, string>): string => {
    const known: string[] = [];
    let unknownCount = 0;
    for (const id of ids) {
      const name = lookup.get(String(id));
      if (name) {
        known.push(name);
      } else {
        unknownCount += 1;
      }
    }
    if (unknownCount === 0) {
      return known.join(', ');
    }
    const missingNote = i18n.t('{{count}} not in this trial', { count: unknownCount })();
    if (known.length === 0) {
      return missingNote;
    }
    return `${known.join(', ')}, ${missingNote}`;
  };

  private plotDetailsSummary = (filters: dashboardApi.SavedViewFilters): string | null => {
    if (this.plotDetailById.size === 0) {
      return null;
    }
    const treatmentId = dashboardApi.TREATMENT_PLOT_DETAIL_ID;
    const dimIds =
      filters.shown_plot_dimension_meta_ids ??
      Array.from(this.plotDetailById.keys()).filter((id) => id !== treatmentId);
    const showTreatment = filters.show_treatment ?? true;
    const ids =
      showTreatment && this.plotDetailById.has(treatmentId) ? [treatmentId, ...dimIds] : dimIds;
    if (ids.length === 0) {
      return i18n.t('none')();
    }
    return this.resolveNames(ids, this.plotDetailById);
  };

  private loadPresets = () => {
    dashboardApi
      .listExportPresets()
      .then((presets) => {
        this.presets(this.sortPresets(presets));
      })
      .catch(() => {
        this.errorMessage(i18n.t('Could not load views. Please try again.')());
      });
  };

  private sortPresets = (
    presets: dashboardApi.OverviewExportPreset[]
  ): dashboardApi.OverviewExportPreset[] => {
    const defaultId = this.defaultPresetId();
    return presets.slice().sort((a, b) => {
      if (a.id === defaultId) return -1;
      if (b.id === defaultId) return 1;
      const aMod = a.modified ?? '';
      const bMod = b.modified ?? '';
      if (aMod < bMod) return 1;
      if (aMod > bMod) return -1;
      return 0;
    });
  };

  loadView = (preset: dashboardApi.OverviewExportPreset) => {
    this.onApply(preset);
    this.result.resolve();
  };

  toggleActionsMenu = (preset: dashboardApi.OverviewExportPreset) => {
    this.openActionsMenuId(this.openActionsMenuId() === preset.id ? null : preset.id);
  };

  closeActionsMenu = () => {
    this.openActionsMenuId(null);
  };

  setAsDefault = (preset: dashboardApi.OverviewExportPreset) => {
    if (!this.canEdit) return;
    this.closeActionsMenu();
    this.errorMessage('');
    trialsApi
      .setDefaultOverviewPreset(this.trialId, preset.id)
      .then((res) => {
        this.defaultPresetId(res.default_overview_preset_id);
        this.onDefaultChanged(res.default_overview_preset_id);
        this.presets(this.sortPresets(this.presets()));
      })
      .catch(() => {
        this.errorMessage(i18n.t('Could not update the default view. Please try again.')());
      });
  };

  deleteView = async (preset: dashboardApi.OverviewExportPreset) => {
    if (!this.canEdit) return;
    this.closeActionsMenu();
    this.errorMessage('');
    try {
      await confirmDialog(
        i18n.t('Delete view')(),
        i18n.t('Delete the view "{{name}}"? This cannot be undone.', { name: preset.name })(),
        'dialog-remove'
      );
    } catch {
      return;
    }
    dashboardApi
      .deleteExportPreset(preset.id)
      .then(() => {
        this.presets.remove(preset);
        if (this.defaultPresetId() === preset.id) {
          this.defaultPresetId(null);
          this.onDefaultChanged(null);
        }
        if (this.activePresetId() === preset.id) {
          this.activePresetId(null);
        }
        this.onPresetDeleted(preset.id);
      })
      .catch(() => {
        this.errorMessage(i18n.t('Could not delete the view. Please try again.')());
      });
  };

  onClose = () => {
    this.result.resolve();
  };
}

ko.components.register('overview-views-dialog', {
  viewModel: OverviewViewsDialog,
  template,
});

export function openOverviewViewsDialog(
  trialId: string,
  defaultPresetId: number | null,
  activePresetId: number | null,
  onApply: (preset: dashboardApi.OverviewExportPreset) => void,
  onDefaultChanged: (presetId: number | null) => void,
  onPresetDeleted: (presetId: number) => void,
  lookups: {
    sites: NameData[];
    scheduledVisits: VisitLookup[];
    traits: TraitLookup[];
    testSubjects: ko.Observable<NameData[] | null>;
    plotDetails: NameData[];
    showIcons: NameData[];
  }
): Promise<void> {
  return app.formsStackController.push({
    title: i18n.t('Saved views')(),
    name: 'overview-views-dialog',
    params: {
      result: new Deferred<void>(),
      trialId,
      defaultPresetId,
      activePresetId,
      onApply,
      onDefaultChanged,
      onPresetDeleted,
      sites: lookups.sites,
      scheduledVisits: lookups.scheduledVisits,
      traits: lookups.traits,
      testSubjects: lookups.testSubjects,
      plotDetails: lookups.plotDetails,
      showIcons: lookups.showIcons,
    },
  });
}
